import _ from 'lodash';
import { omit } from 'ramda';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { APIResponse } from 'spw-redux/types';
import { handleError } from 'spw-redux/modules/UI/messages/actions';
import {
  fetchCurrentWorksheet,
  fetchProjectWorksheets,
  getProjectScenarios,
} from 'spw-redux/modules/calculators/actions';
import {
  currentProject,
  projectSpaceTypes as projectSpaceTypesSelector,
  selectProjectSpaceTypeCategories,
} from 'spw-redux/modules/projects/selectors';
import { selectProjectSettings } from 'spw-redux/modules/settings/selector';
import {
  IProject,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import {
  getScenariosContainingSpaceType as getScenariosContainingSpaceTypeApi,
  updateWorksheetRowsBySpaceType as updateWorksheetRowsBySpaceTypeApi,
} from 'spw-api/programming-program-summary';
import { IChildWorksheetSpaceType } from 'spw-redux/modules/calculators/types';
import { IProjectSpaceType } from 'spw-redux/modules/space_types/types';
import { IProjectSettings } from 'spw-redux/modules/settings/types';
import {
  selectAllChildWorksheetSpaces,
  selectScenarios,
} from 'spw-redux/modules/calculators/selectors';
import { hydrateWorksheetRows } from 'spw-redux/modules/calculators/utils';
import Worksheet from '../../../../models/Worksheet';
import { PlainProgramScenario } from '../../../../models/Scenario';
import {
  actions,
  GET_WORKSHEETS_USING_SPACE_TYPE,
  UPDATE_WORKSHEET_ROWS_BY_SPACE_TYPE,
} from './actions';

function* getScenariosContainingSpaceType(
  action: ReturnType<typeof actions.getWorksheetsUsingSpaceType>
) {
  try {
    const { projectSpaceType } = action.payload;
    const { projectId } = projectSpaceType;
    const spaceTypeId = projectSpaceType.spaceType.id;
    const isChild = !!projectSpaceType.spaceType.parent;

    const scenarios: PlainProgramScenario[] = yield select(selectScenarios);
    const projectSettings: IProjectSettings = yield select(
      selectProjectSettings
    );
    const projectSpaceTypesCategories: IProjectSpaceTypeCategory[] = yield select(
      selectProjectSpaceTypeCategories
    );
    const childWorksheetSpaces: IChildWorksheetSpaceType[] = yield select(
      selectAllChildWorksheetSpaces
    );
    const projectSpaceTypes: IProjectSpaceType[] = yield select(
      projectSpaceTypesSelector
    );

    const {
      data: { scenarios: scenariosContainingSpaceType },
    }: APIResponse<typeof getScenariosContainingSpaceTypeApi> = yield call(
      getScenariosContainingSpaceTypeApi,
      projectId,
      spaceTypeId,
      isChild
    );

    const worksheets = scenariosContainingSpaceType.map((scenario) => {
      return new Worksheet(
        {
          ...scenario.worksheet,
          rows: hydrateWorksheetRows({
            projectSpaceTypes,
            rows: scenario.worksheet.rows,
            childWorksheetSpaces,
            projectSpaceTypesCategories,
          }),
        },
        scenarios.find((s) => s.id === scenario.id),
        projectSettings
      ).toPlainObject();
    });

    yield put(actions.getWorksheetsUsingSpaceTypeSucceded(worksheets));
  } catch (error) {
    yield put(handleError(error));
  }
}

function* updateWorksheetRowsBySpaceType(
  action: ReturnType<typeof actions.updateWorksheetRowsBySpaceType>
) {
  const project: IProject = yield select(currentProject);

  try {
    yield call(
      updateWorksheetRowsBySpaceTypeApi,
      omit(['callback'], action.payload)
    );

    yield put(getProjectScenarios(project.id));
    yield put(fetchProjectWorksheets(action.payload.scenarioIds));
    yield put(fetchCurrentWorksheet());
    action.payload.callback();
  } catch (error) {
    yield put(handleError(error));
  }
}

function* saga() {
  yield all([
    takeLatest(
      GET_WORKSHEETS_USING_SPACE_TYPE,
      getScenariosContainingSpaceType
    ),
    takeLatest(
      UPDATE_WORKSHEET_ROWS_BY_SPACE_TYPE,
      updateWorksheetRowsBySpaceType
    ),
  ]);
}

export default saga;
